import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, LogOut, Plus, Loader2, Film, ArrowLeft } from "lucide-react";
import { services } from "../data";
import { API } from "@/lib/api";

const TOKEN_KEY = "c7_admin_token";

const serviceName = (id) => services.find((s) => s.id === id)?.title || id;

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const [videos, setVideos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    service_id: services[0].id,
    title: "",
    url: "",
    thumbnail: "",
  });

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setVideos([]);
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/videos`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVideos(data);
    } catch (e) {
      if (e.response?.status === 401) logout();
    }
  }, [token, logout]);

  useEffect(() => {
    if (!token) return undefined;
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/videos`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (active) setVideos(data);
      } catch (e) {
        if (e.response?.status === 401) logout();
      }
    })();
    return () => {
      active = false;
    };
  }, [token, logout]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword("");
      toast.success("Welcome back");
    } catch {
      toast.error("Incorrect password");
    } finally {
      setLoading(false);
    }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.url.trim()) {
      toast.error("Title and video URL are required");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/videos`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Video added");
      setForm({ ...form, title: "", url: "", thumbnail: "" });
      load();
    } catch (e) {
      if (e.response?.status === 401) logout();
      else toast.error(e.response?.data?.detail || "Could not add video");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/videos/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Removed");
      load();
    } catch {
      toast.error("Could not remove");
    }
  };

  // ---- Login screen ----
  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-sm rounded-3xl border border-line bg-surface p-8"
        >
          <div className="flex items-center gap-2 text-accent">
            <Film className="h-5 w-5" />
            <span className="text-xs font-semibold uppercase tracking-[0.2em]">Catalyst Admin</span>
          </div>
          <h1 className="font-display mt-4 text-2xl font-bold text-ink">Manage Showreels</h1>
          <p className="mt-2 text-sm text-[#8C8C8C]">Enter the admin password to add or remove videos.</p>

          <label className="mt-6 block text-xs font-semibold uppercase tracking-[0.12em] text-ink/70">
            Password
          </label>
          <input
            data-testid="admin-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="mt-2 w-full rounded-2xl border border-line bg-white/5 px-4 py-3 text-sm text-ink outline-none transition-colors placeholder:text-white/30 focus:border-accent"
          />

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-accent px-6 py-3 text-sm font-semibold text-[#04130F] transition-colors hover:bg-accent-dark disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
          </button>

          <a href="/" className="mt-5 flex items-center justify-center gap-1.5 text-xs font-medium text-ink/50 hover:text-ink">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to site
          </a>
        </form>
      </div>
    );
  }

  // ---- Dashboard ----
  return (
    <div className="min-h-screen px-6 py-12 md:px-10">
      <div className="mx-auto max-w-4xl">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Catalyst Admin</p>
            <h1 className="font-display mt-2 text-3xl font-bold text-ink">Showreel Manager</h1>
          </div>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="inline-flex items-center gap-2 rounded-full border border-line px-4 py-2 text-sm font-semibold text-ink/80 transition-colors hover:border-ink/40"
          >
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>

        {/* Add form */}
        <form
          onSubmit={add}
          data-testid="admin-add-form"
          className="mt-8 rounded-3xl border border-line bg-surface p-6 md:p-8"
        >
          <h2 className="font-display text-lg font-bold text-ink">Add a video</h2>
          <p className="mt-1 text-sm text-[#8C8C8C]">Paste a Vimeo, YouTube or direct video (.mp4) link.</p>

          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold uppercase tracking-[0.12em] text-ink/70">Category</label>
              <select
                data-testid="admin-video-service"
                value={form.service_id}
                onChange={(e) => setForm({ ...form, service_id: e.target.value })}
                className="rounded-2xl border border-line bg-white/5 px-4 py-3 text-sm text-ink outline-none focus:border-accent"
              >
                {services.map((s) => (
                  <option key={s.id} value={s.id} className="bg-[#101714] text-ink">
                    {s.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold uppercase tracking-[0.12em] text-ink/70">Title</label>
              <input
                data-testid="admin-video-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. DBS Bank Brand Film"
                className="rounded-2xl border border-line bg-white/5 px-4 py-3 text-sm text-ink outline-none placeholder:text-white/30 focus:border-accent"
              />
            </div>
            <div className="flex flex-col gap-2 sm:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-[0.12em] text-ink/70">Video URL</label>
              <input
                data-testid="admin-video-url"
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder="https://vimeo.com/...  ·  https://youtu.be/...  ·  https://.../film.mp4"
                className="rounded-2xl border border-line bg-white/5 px-4 py-3 text-sm text-ink outline-none placeholder:text-white/30 focus:border-accent"
              />
            </div>
            <div className="flex flex-col gap-2 sm:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-[0.12em] text-ink/70">
                Thumbnail URL <span className="text-white/30">(optional)</span>
              </label>
              <input
                data-testid="admin-video-thumb"
                value={form.thumbnail}
                onChange={(e) => setForm({ ...form, thumbnail: e.target.value })}
                placeholder="Cover image for the card (YouTube auto-fills)"
                className="rounded-2xl border border-line bg-white/5 px-4 py-3 text-sm text-ink outline-none placeholder:text-white/30 focus:border-accent"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={saving}
            data-testid="admin-add-btn"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-accent px-6 py-3 text-sm font-semibold text-[#04130F] transition-colors hover:bg-accent-dark disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Add Video
          </button>
        </form>

        {/* Video list */}
        <div className="mt-10">
          <h2 className="font-display text-lg font-bold text-ink">
            All Videos <span className="text-white/30">({videos.length})</span>
          </h2>
          <div className="mt-4 space-y-3">
            {videos.length === 0 && (
              <p className="rounded-2xl border border-dashed border-line p-6 text-center text-sm text-[#8C8C8C]">
                No videos yet. Add your first one above.
              </p>
            )}
            {videos.map((v) => (
              <div
                key={v.id}
                data-testid={`admin-video-item-${v.id}`}
                className="flex items-center gap-4 rounded-2xl border border-line bg-surface p-4"
              >
                <div className="h-14 w-20 shrink-0 overflow-hidden rounded-lg bg-white/5">
                  {v.thumbnail ? (
                    <img src={v.thumbnail} alt={v.title} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-white/30">
                      <Film className="h-5 w-5" />
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-ink">{v.title}</p>
                  <p className="truncate text-xs text-[#8C8C8C]">
                    {serviceName(v.service_id)} · <span className="uppercase">{v.provider}</span>
                  </p>
                </div>
                <button
                  data-testid={`admin-delete-${v.id}`}
                  onClick={() => remove(v.id)}
                  className="flex h-9 w-9 items-center justify-center rounded-full text-ink/60 transition-colors hover:bg-accent/10 hover:text-accent"
                  aria-label="Delete video"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
